/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";

function PaymentGateways() {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    business_unit_id: "",
    hotel_email: "",
    country: "ES",
    account_type: "express", // express | standard
  });

  const [createdAccountId, setCreatedAccountId] = useState("");
  const [onboardingUrl, setOnboardingUrl] = useState("");
  const [onboardingExpiresAt, setOnboardingExpiresAt] = useState(null);
  const [connections, setConnections] = useState([]);

  // 🆕 Map: business_unit_id -> Stripe account info
  const [acctInfoByBuId, setAcctInfoByBuId] = useState({});

  // 🆕 Existing Stripe accounts (from platform)
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [existingAccountsLoading, setExistingAccountsLoading] = useState(false);
  const [selectedExistingAccountId, setSelectedExistingAccountId] = useState("");

  
  useEffect(() => {
    async function load() {
      try {
        setErr("");
        setLoading(true);

        const buRes = await axios.get("/api/business-units");
        setBusinessUnits(Array.isArray(buRes.data) ? buRes.data : []);

        const conRes = await axios.get("/api/stripe-connect/business-units");
        const conRows = Array.isArray(conRes.data) ? conRes.data : [];
        setConnections(conRows);

        // 🆕 Fetch real Stripe status for each connected BU
        const nextMap = {};

        await Promise.all(
          conRows.map(async (r) => {
            const buId = Number(r.business_unit_id);
            const acct = String(r.stripe_connected_account_id || "").trim();
            if (!buId || !acct) return;

            try {
              const resp = await axios.get(`/api/stripe-connect/accounts/${encodeURIComponent(acct)}`);
              nextMap[buId] = resp.data || null;

              console.log("[PAYMENT_GATEWAYS][ACCT_STATUS][OK]", {
                business_unit_id: buId,
                account_id: acct,
                charges_enabled: !!resp.data?.charges_enabled,
                payouts_enabled: !!resp.data?.payouts_enabled,
                details_submitted: !!resp.data?.details_submitted,
              });
            } catch (e) {
              console.warn("[PAYMENT_GATEWAYS][ACCT_STATUS][ERR]", {
                business_unit_id: buId,
                account_id: acct,
                err: e?.response?.data || e?.message || e,
              });
              nextMap[buId] = { error: true };
            }
          })
        );

        setAcctInfoByBuId(nextMap);

        await loadExistingAccounts();

        console.log("[PAYMENT_GATEWAYS][LOAD][OK]", {
          bus: (buRes.data || []).length,
          connections: conRows.length,
          statusFetched: Object.keys(nextMap).length,
        });

      } catch (e) {
        console.error("[PAYMENT_GATEWAYS][LOAD][ERR]", e?.message || e);
        setErr("Error loading Business Units");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

    const loadExistingAccounts = async () => {
    try {
      setExistingAccountsLoading(true);
      setErr("");

      const res = await axios.get("/api/stripe-connect/accounts?limit=100");
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];

      setExistingAccounts(rows);

      console.log("[PAYMENT_GATEWAYS][EXISTING_ACCOUNTS][OK]", { count: rows.length });
    } catch (e) {
      console.error("[PAYMENT_GATEWAYS][EXISTING_ACCOUNTS][ERR]", e?.response?.data || e?.message || e);
      setErr("Error loading Stripe accounts");
    } finally {
      setExistingAccountsLoading(false);
    }
  };



  const handleCreateConnectedAccount = async () => {
    try {
        setErr("");
        setCreatedAccountId("");
        setOnboardingUrl("");
        setOnboardingExpiresAt(null);

        // ✅ must pick BU first (otherwise no place to persist)
        const buId = Number(form.business_unit_id);
        if (!Number.isFinite(buId) || buId <= 0) {
        setErr("Select a Business Unit first");
        return;
        }

        const payload = {
        type: String(form.account_type || "express").trim(),
        country: String(form.country || "ES").trim(),
        email: String(form.hotel_email || "").trim() || undefined,
        business_profile: {
            name: undefined,
            url: undefined,
            product_description: "Hotel payments via Pilcore",
        },
        };

        console.log("[PAYMENT_GATEWAYS][CREATE_ACCOUNT][POST][IN]", { buId, payload });

        const res = await axios.post("/api/stripe-connect/accounts", payload);

        const account_id = String(res.data?.account_id || "").trim();
        if (!account_id) {
        setErr("Stripe did not return account_id");
        console.warn("[PAYMENT_GATEWAYS][CREATE_ACCOUNT][WARN] missing account_id", res.data);
        return;
        }

        setCreatedAccountId(account_id);

        // ✅ AUTO-SAVE to hotel_settings so you don't lose it after Stripe redirect
        console.log("[PAYMENT_GATEWAYS][BU_SAVE][PUT][START]", { buId, account_id });

        await axios.put(`/api/hotel-settings/${buId}`, {
        stripe_connected_account_id: account_id,
        });

        console.log("[PAYMENT_GATEWAYS][BU_SAVE][PUT][OK]", { buId, account_id });

        console.log("[PAYMENT_GATEWAYS][CREATE_ACCOUNT][OK]", { account_id });
    } catch (e) {
        console.error("[PAYMENT_GATEWAYS][CREATE_ACCOUNT][ERR]", e?.response?.data || e?.message || e);
        setErr("Error creating connected account");
    }
    };


    const handleCreateOnboardingLink = async () => {
    try {
      setErr("");
      setOnboardingUrl("");
      setOnboardingExpiresAt(null);

      if (!createdAccountId) {
        setErr("Create a connected account first");
        return;
      }

      // NOTE: put something safe that exists in your frontend
      const origin = window.location.origin;
      const refresh_url = `${origin}/admin/integrations/payment-gateways`;
      const return_url = `${origin}/admin/integrations/payment-gateways`;

      const payload = {
        account_id: createdAccountId,
        refresh_url,
        return_url,
        type: "account_onboarding",
      };

      console.log("[PAYMENT_GATEWAYS][ACCOUNT_LINK][POST][IN]", payload);

      const res = await axios.post("/api/stripe-connect/account-links", payload);

      const url = String(res.data?.url || "").trim();
      const expires_at = res.data?.expires_at ?? null;

      if (!url) {
        setErr("Stripe did not return an onboarding URL");
        console.warn("[PAYMENT_GATEWAYS][ACCOUNT_LINK][WARN] missing url", res.data);
        return;
      }

      setOnboardingUrl(url);
      setOnboardingExpiresAt(expires_at);

      console.log("[PAYMENT_GATEWAYS][ACCOUNT_LINK][OK]", { expires_at });
    } catch (e) {
      console.error("[PAYMENT_GATEWAYS][ACCOUNT_LINK][ERR]", e?.response?.data || e?.message || e);
      setErr("Error creating onboarding link");
    }
  };

    const handleSaveToBusinessUnit = async () => {
    try {
      setErr("");

      const business_unit_id = Number(form.business_unit_id);
      if (!Number.isFinite(business_unit_id) || business_unit_id <= 0) {
        setErr("Select a Business Unit first");
        return;
      }

      const acct = String(createdAccountId || "").trim();
      if (!acct) {
        setErr("Create a connected account first");
        return;
      }

      console.log("[PAYMENT_GATEWAYS][SAVE][HOTEL_SETTINGS][PUT][IN]", {
        business_unit_id,
        stripe_connected_account_id: acct,
      });

      await axios.put(`/api/hotel-settings/${business_unit_id}`, {
        stripe_connected_account_id: acct,
      });

      console.log("[PAYMENT_GATEWAYS][SAVE][HOTEL_SETTINGS][PUT][OK]", { business_unit_id, acct });

      alert("Saved to Business Unit");
    } catch (e) {
      console.error("[PAYMENT_GATEWAYS][SAVE][HOTEL_SETTINGS][PUT][ERR]", e?.response?.data || e?.message || e);
      setErr("Error saving to Business Unit");
    }
  };

    const handleLinkExistingAccountToBusinessUnit = async () => {
    try {
      setErr("");

      const buId = Number(form.business_unit_id);
      if (!Number.isFinite(buId) || buId <= 0) {
        setErr("Select a Business Unit first");
        return;
      }

      const acct = String(selectedExistingAccountId || "").trim();
      if (!acct) {
        setErr("Select an existing Stripe account first");
        return;
      }

      console.log("[PAYMENT_GATEWAYS][LINK_EXISTING][PUT][IN]", { buId, acct });

      await axios.put(`/api/hotel-settings/${buId}`, {
        stripe_connected_account_id: acct,
      });

      // reflect in UI
      setCreatedAccountId(acct);

      // optional: refresh list/table status
      await loadExistingAccounts();

      alert("Linked existing Stripe account to Business Unit");
    } catch (e) {
      console.error("[PAYMENT_GATEWAYS][LINK_EXISTING][ERR]", e?.response?.data || e?.message || e);
      setErr("Error linking existing Stripe account");
    }
  };



  if (loading) return <p>Loading…</p>;
  if (err) return <p style={{ color: "red" }}>{err}</p>;

  return (
    <div>
      <h2 className="mb-3">Payment Gateways</h2>

      <div className="mb-3" style={{ maxWidth: 720 }}>
        <div className="mb-2">
          <label className="form-label">Business Unit</label>
          <select
            className="form-select"
            style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
            value={form.business_unit_id}
            onChange={(e) => {
                const v = e.target.value;
                setForm((p) => ({ ...p, business_unit_id: v }));

                const row = (connections || []).find((x) => String(x.business_unit_id) === String(v));
                const acct = String(row?.stripe_connected_account_id || "").trim();

                setCreatedAccountId(acct || "");
                setOnboardingUrl("");
                setOnboardingExpiresAt(null);

                console.log("[PAYMENT_GATEWAYS][BU_SELECT]", { business_unit_id: v, acct: acct || null });
                }}
          >
            <option value="">-- Select Business Unit --</option>
            {businessUnits.map((bu) => (
              <option key={bu.id} value={bu.id}>
                {bu.name || `BU ${bu.id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-2">
          <label className="form-label">Hotel Email (optional)</label>
          <input
            type="email"
            className="form-control"
            style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
            value={form.hotel_email}
            onChange={(e) => setForm((p) => ({ ...p, hotel_email: e.target.value }))}
            placeholder="hotel@example.com"
          />
        </div>

        <div className="d-flex gap-2 mb-2">
          <div style={{ flex: 1 }}>
            <label className="form-label">Country</label>
            <input
              type="text"
              className="form-control"
              style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
              value={form.country}
              onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
              placeholder="ES"
            />
          </div>

          <div style={{ flex: 1 }}>
            <label className="form-label">Account Type</label>
            <select
              className="form-select"
              style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
              value={form.account_type}
              onChange={(e) => setForm((p) => ({ ...p, account_type: e.target.value }))}
            >
              <option value="express">Express</option>
              <option value="standard">Standard</option>
            </select>
          </div>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-warning" onClick={handleCreateConnectedAccount}>
            Create Connected Account
          </button>

          <button className="btn btn-secondary" onClick={handleCreateOnboardingLink} disabled={!createdAccountId}>
            Create Onboarding Link
          </button>

          <button
            className="btn btn-success"
            onClick={handleSaveToBusinessUnit}
            disabled={!createdAccountId || !String(form.business_unit_id || "").trim()}
          >
            Save to Business Unit
          </button>
        </div>

      </div>

      <div className="mt-3" style={{ maxWidth: 980 }}>
        <div className="mb-2">
          <div style={{ fontSize: "0.85rem", color: "#bbb" }}>Connected account id</div>
          <div style={{ fontFamily: "monospace" }}>{createdAccountId || "-"}</div>
        </div>

        <div className="mb-2">
          <div style={{ fontSize: "0.85rem", color: "#bbb" }}>Onboarding link</div>
          {onboardingUrl ? (
            <div>
              <a href={onboardingUrl} target="_blank" rel="noreferrer">
                Open onboarding
              </a>
              {onboardingExpiresAt ? (
                <div style={{ fontSize: "0.8rem", color: "#888" }}>
                  Expires at: {String(onboardingExpiresAt)}
                </div>
              ) : null}
            </div>
          ) : (
            <div>-</div>
          )}
        </div>

        <h3 className="mt-4">Business Units - Stripe Connect</h3>

        <div className="mb-3 p-2 border border-secondary rounded" style={{ backgroundColor: "#111" }}>
          <div className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600 }}>
            Link an existing connected account to the selected Business Unit
          </div>

          <div className="d-flex gap-2 align-items-center">
            <select
              className="form-select"
              style={{ backgroundColor: "#333", color: "white", border: "1px solid #555", maxWidth: 520 }}
              value={selectedExistingAccountId}
              onChange={(e) => setSelectedExistingAccountId(e.target.value)}
              disabled={existingAccountsLoading}
            >
              <option value="">{existingAccountsLoading ? "Loading accounts..." : "-- Select existing Stripe account --"}</option>

              {(existingAccounts || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.id} {a.email ? `— ${a.email}` : ""} {a.details_submitted ? "" : " (needs onboarding)"}
                </option>
              ))}
            </select>

            <button
              className="btn btn-success"
              onClick={handleLinkExistingAccountToBusinessUnit}
              disabled={!String(form.business_unit_id || "").trim() || !String(selectedExistingAccountId || "").trim()}
            >
              Link to selected BU
            </button>

            <button className="btn btn-secondary" onClick={loadExistingAccounts} disabled={existingAccountsLoading}>
              Refresh
            </button>
          </div>

          <div className="mt-2" style={{ fontSize: "0.85rem", color: "#bbb" }}>
            Tip: select a BU above first, then link an account. This writes hotel_settings.stripe_connected_account_id.
          </div>
        </div>

        <table className="table table-dark table-striped align-middle" style={{ maxWidth: 980 }}>

          <thead>
            <tr>
              <th>Business Unit</th>
              <th>Connected Account</th>
              <th>details_submitted</th>
              <th>charges_enabled</th>
              <th>payouts_enabled</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {(connections || []).map((r) => {
              const buId = Number(r.business_unit_id);
              const acct = String(r.stripe_connected_account_id || "").trim();

              const info = buId ? acctInfoByBuId[buId] : null;
              const hasAcct = !!acct;

              const details_submitted = !!info?.details_submitted;
              const charges_enabled = !!info?.charges_enabled;
              const payouts_enabled = !!info?.payouts_enabled;

              let status = "Not connected";
              if (hasAcct) {
                status = (details_submitted && charges_enabled) ? "Connected" : "Pending";
                if (info?.error) status = "Error";
              }

              return (
                <tr key={r.business_unit_id}>
                  <td>{r.business_unit_name}</td>

                  <td style={{ fontFamily: "monospace" }}>{acct || "-"}</td>

                  <td>{hasAcct ? (info?.error ? "-" : String(details_submitted)) : "-"}</td>
                  <td>{hasAcct ? (info?.error ? "-" : String(charges_enabled)) : "-"}</td>
                  <td>{hasAcct ? (info?.error ? "-" : String(payouts_enabled)) : "-"}</td>

                  <td>{status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>


      </div>
    </div>
  );
}

export default PaymentGateways;
